import { useMemo } from "react";
import { Receipt, CalendarRange, AlertTriangle, Clock, Sparkles } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

/**
 * Compute the next fiscal deadline(s) the owner should act on and pick the
 * closest one (within a 60-day horizon). 100% client-side — the backend
 * schedulers already deliver in-app/push nudges in parallel.
 */
function nextFiscalDeadline(today = new Date()) {
  const candidates = [];

  // Quarterly (Modelo 303 IVA): filing in Jan/Apr/Jul/Oct, day 20 (or 30 for Q4)
  for (let offset = 0; offset <= 12; offset++) {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const m = d.getMonth();         // 0-11
    if (![0, 3, 6, 9].includes(m)) continue;
    const filingYear = d.getFullYear();
    const quarter = m === 0 ? 4 : m / 3;
    const quarterYear = m === 0 ? filingYear - 1 : filingYear;
    const deadlineDay = m === 0 ? 30 : 20;
    const deadline = new Date(filingYear, m, deadlineDay, 23, 59, 59);
    if (deadline >= today) {
      candidates.push({
        kind: "quarter",
        label: `Modelo 303 IVA · ${quarterYear}-Q${quarter}`,
        sub: `Trimestre ${quarter} de ${quarterYear}`,
        deadline,
        year: quarterYear,
        quarter,
        cta: "IVA trimestral",
        icon: Receipt,
      });
    }
  }

  // Annual (cierre fiscal — cut-off típico 25-Ene para entregar al asesor)
  for (let yOffset = 0; yOffset <= 1; yOffset++) {
    const filingYear = today.getFullYear() + yOffset;
    const deadline = new Date(filingYear, 0, 25, 23, 59, 59);
    if (deadline >= today) {
      candidates.push({
        kind: "year",
        label: `Cierre fiscal ${filingYear - 1}`,
        sub: `Año completo ${filingYear - 1}`,
        deadline,
        year: filingYear - 1,
        cta: "Enviar año completo",
        icon: CalendarRange,
      });
    }
  }

  candidates.sort((a, b) => a.deadline - b.deadline);
  const nearest = candidates[0];
  if (!nearest) return null;
  const daysLeft = Math.ceil((nearest.deadline.getTime() - today.getTime()) / 86400000);
  if (daysLeft > 60) return null;   // hide if too far out
  return { ...nearest, daysLeft };
}

const triggerTestIdByKind = {
  quarter: "export-quarter-accountant-btn",
  year: "export-annual-accountant-btn",
};

export default function NextFiscalDeadlineCard() {
  const d = useMemo(() => nextFiscalDeadline(), []);
  if (!d) return null;

  const urgent = d.daysLeft <= 5;
  const warn = d.daysLeft <= 15;
  const accent = urgent
    ? "from-red-500/10 to-red-500/5 border-red-300"
    : warn
      ? "from-amber-500/10 to-amber-500/5 border-amber-300"
      : "from-indigo-500/10 to-indigo-500/5 border-indigo-200";

  const Icon = d.icon;
  const deadlineLabel = d.deadline.toLocaleDateString("es-ES", {
    day: "numeric", month: "long", year: "numeric",
  });

  const onOpen = () => {
    const btn = document.querySelector(`[data-testid="${triggerTestIdByKind[d.kind]}"]`);
    if (btn) {
      btn.click();
      btn.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <Card
      className={`border bg-gradient-to-br ${accent} shadow-soft`}
      data-testid="next-fiscal-deadline-card"
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${urgent ? "bg-red-500/15" : warn ? "bg-amber-500/15" : "bg-indigo-500/15"}`}>
          <Icon className={`h-5 w-5 ${urgent ? "text-red-700" : warn ? "text-amber-700" : "text-indigo-600"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${urgent ? "text-red-700" : warn ? "text-amber-700" : "text-indigo-700"}`}>
              Próximo deadline fiscal
            </span>
            {urgent && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-500 text-white">
                <AlertTriangle className="h-2.5 w-2.5" />
                Urgente
              </span>
            )}
            {!urgent && warn && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-500 text-white">
                <Clock className="h-2.5 w-2.5" />
                Esta quincena
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-foreground mt-0.5 truncate">
            {d.label}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Vence el <b>{deadlineLabel}</b> · {d.daysLeft > 0 ? `${d.daysLeft} día${d.daysLeft === 1 ? "" : "s"} restantes` : "Hoy es el último día"}
          </div>
        </div>
        <Button
          size="sm"
          variant={urgent ? "default" : "outline"}
          className={urgent ? "bg-red-600 hover:bg-red-700 text-white shrink-0" : "shrink-0"}
          onClick={onOpen}
          data-testid="next-fiscal-deadline-cta"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          {d.cta}
        </Button>
      </CardContent>
    </Card>
  );
}
